import React, { useState, useContext, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { apiClient } from '../services/api';
import { getCustomerId, hydrateUser } from '../utils/authUser';

const PRODUCT_LOGO_URL = 'https://d7umqicpi7263.cloudfront.net/img/product/2b18747e-3ac1-44a0-9139-651800f162e8.com/895833868c0d761a640f9b58119d927f';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('login'); // login, newPassword, forgotPassword, confirmCode
  const [verificationCode, setVerificationCode] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(AuthContext);
  const bindingToken = new URLSearchParams(location.search).get('binding_token');
  const BINDING_TOKEN_STORAGE_KEY = 'marketplace_binding_token';

  const clearStoredBindingToken = () => {
    try {
      window.localStorage.removeItem(BINDING_TOKEN_STORAGE_KEY);
    } catch (storageError) {
      console.warn('Failed to clear binding token from storage:', storageError);
    }
  };

  useEffect(() => {
    try {
      if (bindingToken) {
        window.localStorage.setItem(BINDING_TOKEN_STORAGE_KEY, bindingToken);
      } else {
        window.localStorage.removeItem(BINDING_TOKEN_STORAGE_KEY);
      }
    } catch (storageError) {
      console.warn('Failed to sync binding token in storage:', storageError);
    }
  }, [bindingToken]);

  useEffect(() => {
    if (!user || !bindingToken) {
      return;
    }

    let active = true;

    const bindExistingSession = async () => {
      try {
        const refreshedUser = await linkMarketplaceCustomerIfNeeded();
        if (active) {
          setUser(refreshedUser || user);
          navigate('/');
        }
      } catch (err) {
        if (active) {
          const errorMessage = err.response?.data?.error || err.message || 'Failed to bind marketplace account';
          setError(errorMessage);
        }
      }
    };

    bindExistingSession();

    return () => {
      active = false;
    };
  }, [user, bindingToken]);

  const linkMarketplaceCustomerIfNeeded = async () => {
    const ensureCustomerProfileIfNeeded = async (currentUser) => {
      if (getCustomerId(currentUser)) {
        return currentUser;
      }

      const emailAddress = currentUser?.attributes?.email || currentUser?.username || '';

      try {
        await apiClient.post('/customers', { email: emailAddress });
      } catch (err) {
        const message = (err.response?.data?.error || err.message || '').toLowerCase();
        if (message.includes('binding_token is required')) {
          throw new Error(
            'This account is not linked to a Marketplace subscription. ' +
            'Please return to AWS Marketplace and click "Set up your account".'
          );
        }
        throw err;
      }

      const refreshed = await Auth.currentAuthenticatedUser({ bypassCache: true });
      return hydrateUser(refreshed);
    };

    let tokenToUse = bindingToken;
    if (!tokenToUse) {
      try {
        tokenToUse = window.localStorage.getItem(BINDING_TOKEN_STORAGE_KEY) || '';
      } catch (storageError) {
        console.warn('Failed to read binding token from storage:', storageError);
      }
    }

    const currentUser = await hydrateUser(await Auth.currentAuthenticatedUser({ bypassCache: true }));
    if (!tokenToUse) {
      return ensureCustomerProfileIfNeeded(currentUser);
    }

    try {
      await apiClient.post('/customers', { binding_token: tokenToUse });
      clearStoredBindingToken();
      const refreshedUser = await hydrateUser(await Auth.currentAuthenticatedUser({ bypassCache: true }));
      return ensureCustomerProfileIfNeeded(refreshedUser);
    } catch (err) {
      const message = (err.response?.data?.error || err.message || '').toLowerCase();
      if (message.includes('already bound to a different customer')) {
        clearStoredBindingToken();
        throw new Error(
          'Wrong user for this Marketplace setup link. ' +
          'Sign in with the portal user linked to this subscription, or sign out and use a different user.'
        );
      }
      if (message.includes('binding token')) {
        clearStoredBindingToken();
        if (getCustomerId(currentUser)) {
          return ensureCustomerProfileIfNeeded(currentUser);
        }
        throw new Error('Marketplace setup link is invalid or expired. Please return to AWS Marketplace and click "Set up your account" again.');
      }
      throw err;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await Auth.signIn(email, password);

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setStage('newPassword');
        setLoading(false);
        return;
      }

      const refreshedUser = await linkMarketplaceCustomerIfNeeded();
      setUser(refreshedUser || (await hydrateUser(user)));
      navigate('/');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to sign in';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const user = await Auth.completeNewPassword(
        await Auth.signIn(email, password),
        newPassword
      );
      const refreshedUser = await linkMarketplaceCustomerIfNeeded();
      setUser(refreshedUser || (await hydrateUser(user)));
      navigate('/');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set new password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await Auth.forgotPassword(email);
      setStage('confirmCode');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await Auth.forgotPasswordSubmit(email, verificationCode, newPassword);
      setStage('login');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleHostedUI = () => {
    if (bindingToken) {
      try {
        window.localStorage.setItem(BINDING_TOKEN_STORAGE_KEY, bindingToken);
      } catch (storageError) {
        console.warn('Failed to persist binding token:', storageError);
      }
    } else {
      clearStoredBindingToken();
    }

    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/callback`;

    if (!cognitoDomain || !clientId) {
      Auth.federatedSignIn();
      return;
    }

    const hostedLoginUrl =
      `https://${cognitoDomain}/login?client_id=${encodeURIComponent(clientId)}` +
      `&response_type=code&scope=${encodeURIComponent('email openid profile')}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.assign(hostedLoginUrl);
  };

  const handleHostedUISignUp = () => {
    if (bindingToken) {
      try {
        window.localStorage.setItem(BINDING_TOKEN_STORAGE_KEY, bindingToken);
      } catch (storageError) {
        console.warn('Failed to persist binding token:', storageError);
      }
    } else {
      clearStoredBindingToken();
    }

    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/callback`;

    if (!cognitoDomain || !clientId) {
      Auth.federatedSignIn();
      return;
    }

    const hostedSignupUrl =
      `https://${cognitoDomain}/signup?client_id=${encodeURIComponent(clientId)}` +
      `&response_type=code&scope=${encodeURIComponent('email openid profile')}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.assign(hostedSignupUrl);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="logo">
          <img className="brand-logo" src={PRODUCT_LOGO_URL} alt="Virtuoso product logo" />
          <h1>Virtuoso<span>SaaS</span></h1>
        </div>

        {stage === 'login' && (
          <>
            <h2>Welcome back</h2>
            <p>Sign in to manage your Virtuoso instances</p>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <button
                onClick={() => setStage('forgotPassword')}
                style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer' }}
              >
                Forgot password?
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '-8px', marginBottom: '20px' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>New here? </span>
              <button
                onClick={handleHostedUISignUp}
                style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', padding: 0 }}
                type="button"
              >
                Create an account
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border-color)' }}>
              <p style={{ marginBottom: '16px', fontSize: '14px' }}>Or continue with</p>
              <button onClick={handleHostedUI} className="btn btn-secondary" style={{ width: '100%' }}>
                Sign in via Hosted Login
              </button>
            </div>
          </>
        )}

        {stage === 'newPassword' && (
          <>
            <h2>Set New Password</h2>
            <p>Please set a new password for your account</p>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleNewPassword}>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Setting password...' : 'Set Password'}
              </button>
            </form>
          </>
        )}

        {stage === 'forgotPassword' && (
          <>
            <h2>Reset Password</h2>
            <p>Enter your email to receive a reset code</p>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="resetEmail">Email</label>
                <input
                  type="email"
                  id="resetEmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => setStage('login')}
                style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer' }}
              >
                Back to login
              </button>
            </div>
          </>
        )}

        {stage === 'confirmCode' && (
          <>
            <h2>Enter Reset Code</h2>
            <p>Check your email for the verification code</p>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleConfirmCode}>
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  type="text"
                  id="code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter code"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPwd">New Password</label>
                <input
                  type="password"
                  id="newPwd"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPwd">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPwd"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
